import React, { useEffect, useRef, useState } from "react";
import {
  Container, Row, Col, Card, CardBody,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Badge, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Button,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import {
  getScrapeStatus, getScrapeJobs,
  startAmazonScrape, startCarrefourScrape, startAllScrape,
  stopScraperJob, runMatcher, forceRunMatcher,
  getProductLinks, addProductLinkAPI, updateProductLinkAPI,
  toggleProductLinkAPI, deleteProductLinkAPI,
} from "../../API/Backend";
import { showToast } from "../../Components/Toast";

const STATUS_COLOR = {
  running:   "warning",
  completed: "success",
  failed:    "danger",
  cancelled: "secondary",
  pending:   "info",
};

const SOURCE_COLOR = { amazon: "primary", carrefour: "success", all: "dark" };

function fmt(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-AE", { dateStyle: "medium", timeStyle: "short" });
}

function duration(start, end) {
  if (!start || !end) return "—";
  const secs = Math.round((new Date(end) - new Date(start)) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}m ${s}s`;
}

const EMPTY_LINK = { url: "", source: "carrefour", label: "", group: "", isActive: true };

export default function Scraper() {
  const [tab, setTab] = useState("controls");

  // ── Controls ──────────────────────────────────────────────────────────────
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = (showSpinner = false) => {
    if (showSpinner) setStatusLoading(true);
    getScrapeStatus()
      .then(({ data }) => { if (data?.data) setStatus(data.data); })
      .catch(() => {})
      .finally(() => setStatusLoading(false));
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const isRunning = status?.status === "running" || status?.status === "pending";

  useEffect(() => {
    if (isRunning) {
      pollRef.current = setInterval(() => fetchStatus(), 5000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [isRunning]);

  // ── Confirm-before-start modal ─────────────────────────────────────────────
  const [confirmScrape, setConfirmScrape] = useState(null); // { fn, label, links, loading }

  const openConfirm = (fn, label) => {
    setConfirmScrape({ fn, label, links: [], loading: true });
    const src = label === "All" ? null : label.toLowerCase();
    const query = { isActive: true, limit: 200 };
    if (src) query.source = src;
    getProductLinks(query)
      .then(({ data }) => setConfirmScrape((s) => s ? { ...s, links: data?.data?.links || [], loading: false } : s))
      .catch(() => setConfirmScrape((s) => s ? { ...s, loading: false } : s));
  };

  const handleScrape = () => {
    if (!confirmScrape) return;
    const { fn, label } = confirmScrape;
    setConfirmScrape(null);
    setActionLoading(label);
    fn()
      .then(({ data }) => {
        showToast("success", "Started", data?.message || `${label} started`);
        setStatus((s) => ({ ...(s || {}), status: "running", source: label.toLowerCase() }));
        setTimeout(() => fetchStatus(), 2000);
      })
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setActionLoading(null));
  };

  const handleStop = () => {
    setActionLoading("stop");
    stopScraperJob()
      .then(({ data }) => {
        showToast("success", "Stopped", data?.message || "Scraper stopped");
        fetchStatus();
      })
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setActionLoading(null));
  };

  const handleMatch = () => {
    setActionLoading("match");
    runMatcher()
      .then(({ data }) => showToast("success", "Matching Done", `Matched: ${data?.data?.matched ?? 0}, Prices refreshed: ${data?.data?.priceRefreshed ?? 0}, Skipped: ${data?.data?.skipped ?? 0}`))
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setActionLoading(null));
  };

  const handleForceMatch = () => {
    setActionLoading("forceMatch");
    forceRunMatcher()
      .then(({ data }) => showToast("success", "Force Match Done", `Matched: ${data?.data?.matched ?? 0}, Prices refreshed: ${data?.data?.priceRefreshed ?? 0}, Skipped: ${data?.data?.skipped ?? 0}`))
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setActionLoading(null));
  };

  // ── Product Links ─────────────────────────────────────────────────────────
  const [links, setLinks] = useState([]);
  const [linksCount, setLinksCount] = useState(0);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksParams, setLinksParams] = useState({ first: 0, rows: 15 });
  const [linkModal, setLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState(EMPTY_LINK);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  const fetchLinks = (params, src, active) => {
    setLinksLoading(true);
    const { first, rows } = params;
    const query = { offset: first, limit: rows };
    if (src) query.source = src;
    if (active !== "") query.isActive = active;
    getProductLinks(query)
      .then(({ data }) => {
        setLinks(data?.data?.links || []);
        setLinksCount(data?.data?.count || 0);
      })
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setLinksLoading(false));
  };

  useEffect(() => {
    if (tab === "links") fetchLinks(linksParams, sourceFilter, activeFilter);
  }, [tab, linksParams, sourceFilter, activeFilter]);

  const openAddLink = () => { setLinkForm(EMPTY_LINK); setEditingLinkId(null); setLinkModal(true); };
  const openEditLink = (link) => { setLinkForm({ url: link.url, source: link.source, label: link.label, group: link.group || "", isActive: link.isActive }); setEditingLinkId(link._id); setLinkModal(true); };

  const saveLink = () => {
    const payload = { ...linkForm, group: linkForm.group || null };
    const call = editingLinkId
      ? updateProductLinkAPI(editingLinkId, payload)
      : addProductLinkAPI(payload);
    call
      .then(({ data }) => {
        if (data?.success) {
          showToast("success", "Saved", data.message);
          setLinkModal(false);
          fetchLinks(linksParams, sourceFilter, activeFilter);
        } else {
          showToast("error", "Failed", data?.message);
        }
      })
      .catch((e) => showToast("error", "Error", e.message));
  };

  const handleToggle = (link) => {
    toggleProductLinkAPI(link._id)
      .then(({ data }) => {
        if (data?.success) fetchLinks(linksParams, sourceFilter, activeFilter);
        else showToast("error", "Failed", data?.message);
      })
      .catch((e) => showToast("error", "Error", e.message));
  };

  const handleDeleteLink = (link) => {
    if (!window.confirm(`Delete link: ${link.url}?`)) return;
    deleteProductLinkAPI(link._id)
      .then(({ data }) => {
        if (data?.success) { showToast("success", "Deleted", "Link removed"); fetchLinks(linksParams, sourceFilter, activeFilter); }
        else showToast("error", "Failed", data?.message);
      })
      .catch((e) => showToast("error", "Error", e.message));
  };

  // ── Job History ───────────────────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsCount, setJobsCount] = useState(0);
  const [jobsParams, setJobsParams] = useState({ first: 0, rows: 15, source: "", status: "" });
  const [expandedRows, setExpandedRows] = useState(null);

  const fetchJobs = (params) => {
    setJobsLoading(true);
    const { first, rows, source, status: st } = params;
    getScrapeJobs({ first, rows, ...(source && { source }), ...(st && { status: st }) })
      .then(({ data }) => {
        setJobs(data?.data?.jobs || []);
        setJobsCount(data?.data?.count || 0);
      })
      .catch((e) => showToast("error", "Error", e.message))
      .finally(() => setJobsLoading(false));
  };

  useEffect(() => {
    if (tab === "history") fetchJobs(jobsParams);
  }, [tab, jobsParams]);

  return (
    <Container fluid>
      <Row className="align-items-center mb-3">
        <Col>
          <h5 className="page-title">Scraper Management</h5>
        </Col>
      </Row>

      <Nav tabs className="mb-3">
        {[["controls", "Controls & Status"], ["links", "Product Links"], ["history", "Job History"]].map(([key, label]) => (
          <NavItem key={key}>
            <NavLink
              className={tab === key ? "active" : ""}
              onClick={() => setTab(key)}
              style={{ cursor: "pointer" }}
            >
              {label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={tab}>

        {/* ── TAB 1: Controls ─────────────────────────────────────── */}
        <TabPane tabId="controls">
          <Row>
            <Col md={5}>
              <Card>
                <CardBody>
                  <h6 className="fw-semibold mb-3">Scraper Actions</h6>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={isRunning || actionLoading}
                      onClick={() => openConfirm(startCarrefourScrape, "Carrefour")}
                    >
                      {actionLoading === "Carrefour" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-store-2-line me-1" />}
                      Start Carrefour
                    </button>

                    <button
                      className="btn btn-warning btn-sm"
                      disabled={isRunning || actionLoading}
                      onClick={() => openConfirm(startAmazonScrape, "Amazon")}
                    >
                      {actionLoading === "Amazon" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-amazon-line me-1" />}
                      Start Amazon
                    </button>

                    <button
                      className="btn btn-dark btn-sm"
                      disabled={isRunning || actionLoading}
                      onClick={() => openConfirm(startAllScrape, "All")}
                    >
                      {actionLoading === "All" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-global-line me-1" />}
                      Start All
                    </button>

                    <button
                      className="btn btn-danger btn-sm"
                      disabled={!isRunning || actionLoading}
                      onClick={handleStop}
                    >
                      {actionLoading === "stop" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-stop-circle-line me-1" />}
                      Stop
                    </button>
                  </div>

                  <hr />

                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-muted" style={{ fontSize: 13 }}>Run product matcher manually</span>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-success btn-sm"
                        disabled={isRunning || actionLoading === "match" || actionLoading === "forceMatch"}
                        onClick={handleMatch}
                      >
                        {actionLoading === "match" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-links-line me-1" />}
                        Run Matcher
                      </button>
                      <button
                        className="btn btn-outline-warning btn-sm"
                        disabled={isRunning || actionLoading === "match" || actionLoading === "forceMatch"}
                        onClick={handleForceMatch}
                        title="Re-match all products ignoring 24h TTL"
                      >
                        {actionLoading === "forceMatch" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="ri-refresh-line me-1" />}
                        Force Re-match
                      </button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={7}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-semibold mb-0">Current Job Status</h6>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => fetchStatus(true)} disabled={statusLoading}>
                      <i className={`ri-refresh-line${statusLoading ? " ri-spin" : ""}`} />
                    </button>
                  </div>

                  {!status ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>No jobs found.</p>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: 13 }}>
                        <div>
                          <span className="text-muted me-1">Source:</span>
                          <Badge color={SOURCE_COLOR[status.source] || "secondary"}>{status.source}</Badge>
                        </div>
                        <div>
                          <span className="text-muted me-1">Status:</span>
                          <Badge color={STATUS_COLOR[status.status] || "secondary"}>{status.status}</Badge>
                        </div>
                        <div>
                          <span className="text-muted me-1">Started:</span>
                          {fmt(status.createdAt)}
                        </div>
                        {status.completedAt && (
                          <div>
                            <span className="text-muted me-1">Duration:</span>
                            {duration(status.createdAt, status.completedAt)}
                          </div>
                        )}
                        <div>
                          <span className="text-muted me-1">Inserted:</span>
                          <strong>{status.totalInserted ?? 0}</strong>
                        </div>
                        <div>
                          <span className="text-muted me-1">Updated:</span>
                          <strong>{status.totalUpdated ?? 0}</strong>
                        </div>
                        <div>
                          <span className="text-muted me-1">Skipped:</span>
                          <strong>{status.totalSkipped ?? 0}</strong>
                        </div>
                      </div>

                      {status.categories?.length > 0 && (
                        <div style={{ overflowX: "auto" }}>
                          <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
                            <thead className="table-light">
                              <tr>
                                <th>Category</th>
                                <th>Source</th>
                                <th>Status</th>
                                <th>Pages</th>
                                <th>Inserted</th>
                                <th>Updated</th>
                                <th>Skipped</th>
                              </tr>
                            </thead>
                            <tbody>
                              {status.categories.map((cat, i) => (
                                <tr key={i}>
                                  <td>{cat.label || cat.url?.split("/").pop()}</td>
                                  <td><Badge color={SOURCE_COLOR[cat.source] || "secondary"} style={{ fontSize: 10 }}>{cat.source}</Badge></td>
                                  <td><Badge color={STATUS_COLOR[cat.status] || "secondary"} style={{ fontSize: 10 }}>{cat.status}</Badge></td>
                                  <td>{cat.pagesScraped ?? "—"}</td>
                                  <td>{cat.productsInserted ?? "—"}</td>
                                  <td>{cat.productsUpdated ?? "—"}</td>
                                  <td>{cat.productsSkipped ?? "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ── TAB 2: Product Links ─────────────────────────────────── */}
        <TabPane tabId="links">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center gap-3 mb-3">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 160 }}
                  value={sourceFilter}
                  onChange={(e) => { setSourceFilter(e.target.value); setLinksParams((p) => ({ ...p, first: 0 })); }}
                >
                  <option value="">All Sources</option>
                  <option value="carrefour">Carrefour</option>
                  <option value="amazon">Amazon</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 140 }}
                  value={activeFilter}
                  onChange={(e) => { setActiveFilter(e.target.value); setLinksParams((p) => ({ ...p, first: 0 })); }}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
                <button className="btn btn-primary btn-sm ms-auto" onClick={openAddLink}>
                  <i className="ri-add-line me-1" />Add Link
                </button>
              </div>

              <DataTable
                value={links}
                loading={linksLoading}
                lazy
                paginator
                first={linksParams.first}
                rows={linksParams.rows}
                totalRecords={linksCount}
                onPage={(e) => setLinksParams((p) => ({ ...p, first: e.first, rows: e.rows }))}
                rowsPerPageOptions={[15, 30, 50]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                emptyMessage="No product links found."
                size="small"
              >
                <Column
                  header="Source"
                  style={{ width: 100 }}
                  body={(row) => <Badge color={SOURCE_COLOR[row.source] || "secondary"}>{row.source}</Badge>}
                />
                <Column field="label" header="Label" />
                <Column
                  header="URL"
                  style={{ maxWidth: 320 }}
                  body={(row) => (
                    <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, wordBreak: "break-all" }}>
                      {row.url}
                    </a>
                  )}
                />
                <Column field="group" header="Group" style={{ width: 120 }} body={(row) => row.group || <span className="text-muted">—</span>} />
                <Column
                  header="Active"
                  style={{ width: 80, textAlign: "center" }}
                  body={(row) => (
                    <div className="form-check form-switch d-flex justify-content-center mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={row.isActive}
                        onChange={() => handleToggle(row)}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  )}
                />
                <Column
                  header="Actions"
                  style={{ width: 120 }}
                  body={(row) => (
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditLink(row)}>
                        <i className="ri-edit-line" />
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteLink(row)}>
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  )}
                />
              </DataTable>
            </CardBody>
          </Card>

          {/* Add / Edit Modal */}
          <Modal isOpen={linkModal} toggle={() => setLinkModal(false)}>
            <ModalHeader toggle={() => setLinkModal(false)}>
              {editingLinkId ? "Edit Product Link" : "Add Product Link"}
            </ModalHeader>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label>URL *</Label>
                  <Input value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://..." />
                </FormGroup>
                <FormGroup>
                  <Label>Source *</Label>
                  <Input type="select" value={linkForm.source} onChange={(e) => setLinkForm({ ...linkForm, source: e.target.value })}>
                    <option value="carrefour">Carrefour</option>
                    <option value="amazon">Amazon</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label>Label</Label>
                  <Input value={linkForm.label} onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} placeholder="e.g. Bakery" />
                </FormGroup>
                <FormGroup>
                  <Label>Group <small className="text-muted">(pairs Amazon + Carrefour for same category)</small></Label>
                  <Input value={linkForm.group} onChange={(e) => setLinkForm({ ...linkForm, group: e.target.value })} placeholder="e.g. bakery" />
                </FormGroup>
                <FormGroup check>
                  <Label check>
                    <Input type="checkbox" checked={linkForm.isActive} onChange={(e) => setLinkForm({ ...linkForm, isActive: e.target.checked })} />
                    {" "}Active
                  </Label>
                </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" outline onClick={() => setLinkModal(false)}>Cancel</Button>
              <Button color="primary" disabled={!linkForm.url || !linkForm.source} onClick={saveLink}>Save</Button>
            </ModalFooter>
          </Modal>
        </TabPane>

        {/* ── TAB 3: Job History ───────────────────────────────────── */}
        <TabPane tabId="history">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center gap-3 mb-3">
                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={jobsParams.source}
                  onChange={(e) => setJobsParams((p) => ({ ...p, source: e.target.value, first: 0 }))}
                >
                  <option value="">All Sources</option>
                  <option value="carrefour">Carrefour</option>
                  <option value="amazon">Amazon</option>
                  <option value="all">All</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 150 }}
                  value={jobsParams.status}
                  onChange={(e) => setJobsParams((p) => ({ ...p, status: e.target.value, first: 0 }))}
                >
                  <option value="">All Statuses</option>
                  <option value="running">Running</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => fetchJobs(jobsParams)}>
                  <i className="ri-refresh-line me-1" />Refresh
                </button>
              </div>

              <DataTable
                value={jobs}
                loading={jobsLoading}
                lazy
                paginator
                first={jobsParams.first}
                rows={jobsParams.rows}
                totalRecords={jobsCount}
                onPage={(e) => setJobsParams((p) => ({ ...p, first: e.first, rows: e.rows }))}
                rowsPerPageOptions={[15, 30, 50]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                emptyMessage="No jobs found."
                size="small"
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                rowExpansionTemplate={(row) => (
                  <div style={{ padding: "0.5rem 1rem" }}>
                    {!row.categories?.length ? (
                      <span className="text-muted" style={{ fontSize: 12 }}>No category breakdown available.</span>
                    ) : (
                      <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                        <thead className="table-light">
                          <tr>
                            <th>Category</th>
                            <th style={{ width: 90 }}>Source</th>
                            <th style={{ width: 100 }}>Status</th>
                            <th style={{ width: 70 }}>Pages</th>
                            <th style={{ width: 80 }}>Inserted</th>
                            <th style={{ width: 80 }}>Updated</th>
                            <th style={{ width: 80 }}>Skipped</th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.categories.map((cat, i) => (
                            <tr key={i}>
                              <td>
                                <a href={cat.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-all" }}>
                                  {cat.label || cat.url?.split("/").pop() || cat.url}
                                </a>
                              </td>
                              <td><Badge color={SOURCE_COLOR[cat.source] || "secondary"} style={{ fontSize: 10 }}>{cat.source}</Badge></td>
                              <td><Badge color={STATUS_COLOR[cat.status] || "secondary"} style={{ fontSize: 10 }}>{cat.status}</Badge></td>
                              <td>{cat.currentPage ?? "—"}</td>
                              <td>{cat.inserted ?? "—"}</td>
                              <td>{cat.updated ?? "—"}</td>
                              <td>{cat.skipped ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              >
                <Column expander style={{ width: 40 }} />
                <Column
                  header="Source"
                  style={{ width: 100 }}
                  body={(row) => jobsLoading ? <Skeleton height="20px" width="60px" /> : <Badge color={SOURCE_COLOR[row.source] || "secondary"}>{row.source}</Badge>}
                />
                <Column
                  header="Status"
                  style={{ width: 110 }}
                  body={(row) => jobsLoading ? <Skeleton height="20px" width="70px" /> : <Badge color={STATUS_COLOR[row.status] || "secondary"}>{row.status}</Badge>}
                />
                <Column
                  header="Started"
                  style={{ width: 170 }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="120px" /> : fmt(row.createdAt)}
                />
                <Column
                  header="Duration"
                  style={{ width: 90 }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="50px" /> : duration(row.createdAt, row.completedAt)}
                />
                <Column
                  header="Inserted"
                  style={{ width: 90, textAlign: "center" }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="40px" /> : <strong>{row.totalInserted ?? 0}</strong>}
                />
                <Column
                  header="Updated"
                  style={{ width: 90, textAlign: "center" }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="40px" /> : <strong>{row.totalUpdated ?? 0}</strong>}
                />
                <Column
                  header="Skipped"
                  style={{ width: 90, textAlign: "center" }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="40px" /> : row.totalSkipped ?? 0}
                />
                <Column
                  header="Categories"
                  style={{ width: 90, textAlign: "center" }}
                  body={(row) => jobsLoading ? <Skeleton height="12px" width="40px" /> : row.categories?.length ?? 0}
                />
              </DataTable>
            </CardBody>
          </Card>
        </TabPane>

      </TabContent>
      {/* ── Confirm-before-start modal ─────────────────────────── */}
      <Modal isOpen={!!confirmScrape} toggle={() => setConfirmScrape(null)} size="lg">
        <ModalHeader toggle={() => setConfirmScrape(null)}>
          Start {confirmScrape?.label} Scraper
        </ModalHeader>
        <ModalBody>
          <p className="mb-2" style={{ fontSize: 13 }}>
            The scraper will run on the following <strong>active</strong> product links:
          </p>
          {confirmScrape?.loading ? (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading active links…
            </div>
          ) : confirmScrape?.links?.length === 0 ? (
            <p className="text-danger" style={{ fontSize: 13 }}>
              No active links found for this source. Activate some links in the Product Links tab first.
            </p>
          ) : (
            <table className="table table-sm table-bordered" style={{ fontSize: 12 }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: 90 }}>Source</th>
                  <th style={{ width: 120 }}>Label</th>
                  <th>URL</th>
                </tr>
              </thead>
              <tbody>
                {(confirmScrape?.links || []).map((lnk) => (
                  <tr key={lnk._id}>
                    <td><Badge color={SOURCE_COLOR[lnk.source] || "secondary"} style={{ fontSize: 10 }}>{lnk.source}</Badge></td>
                    <td>{lnk.label || <span className="text-muted">—</span>}</td>
                    <td>
                      <a href={lnk.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-all" }}>
                        {lnk.url}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setConfirmScrape(null)}>Cancel</Button>
          <Button
            color="primary"
            disabled={confirmScrape?.loading || !confirmScrape?.links?.length}
            onClick={handleScrape}
          >
            Confirm & Start
          </Button>
        </ModalFooter>
      </Modal>

    </Container>
  );
}
